// React
import React, { Component } from 'react'
import PropTypes from 'prop-types'

// Packages
import {
  AppBar,
  CssBaseline,
  Grid,
  IconButton,
  Link,
  ThemeProvider,
  Toolbar,
  Typography
} from '@mui/material'
import { createTheme } from '@mui/material/styles'
import withWidth, {
  isWidthUp
} from '../utils/withWidth'
import {
  ArrowBack,
  Email,
  Home,
  Phone
} from '@mui/icons-material'
import { observer, inject } from 'mobx-react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { FormattedMessage } from 'react-intl'
import {
  Link as RouterLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom'

// Internal
import BookPage from './BookPage'
import HomePage from './HomePage'
import ListingPage from './ListingPage'

// Assets
import LogoSPI from '../assets/Logo-SepteoADB.png'

const withRouter = WrappedComponent => function RouterComponent (props) {
  const location = useLocation()
  const navigate = useNavigate()
  return <WrappedComponent {...props} location={location} navigate={navigate} />
}

const ListingPageRoute = props => {
  const location = useLocation()
  const params = useParams()
  return <ListingPage {...props} location={location} match={{ params }} />
}

const BookPageRoute = props => {
  const location = useLocation()
  const params = useParams()
  return <BookPage {...props} location={location} match={{ params }} />
}

@withWidth()
@inject('applicationStore')
@observer
class App extends Component {
  static propTypes = {
    applicationStore: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    width: PropTypes.string.isRequired
  }

  componentDidMount = () => {
    this.props.applicationStore.getAgenciesFromSubdomain(window.location.hostname).then(() => {
      this.props.applicationStore.getDestinationsFromAgencies()
      this.props.applicationStore.getListingsFromAgencies()
      this.props.applicationStore.getListingTypesFromAgencies()
      this.props.applicationStore.getMainAgencyLogo()
      this.props.applicationStore.getMainAgencyBackdrop()
    })
  }

  handleBackClicked = () => this.props.navigate(-1)

  render () {
    const store = this.props.applicationStore
    const mainAgency = store.agencies[store.mainAgencyId]

    if (store.mainAgencyId === '') {
      if (store.agencies.error) return <p>Error :(</p>
      return <p>Loading</p>
    } else {
      const theme = createTheme({
        palette: {
          primary: {
            main: mainAgency.mainColor
          },
          secondary: {
            main: mainAgency.accentColor
          }
        }
      })

      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <div>
            <AppBar>
              <Toolbar>
                {
                  this.props.location.pathname !== '/' && (
                    <>
                      {window.history.length > 1 && (
                        <IconButton color='inherit' aria-label='Back' onClick={this.handleBackClicked}>
                          <ArrowBack />
                        </IconButton>
                      )}
                      <IconButton color='inherit' aria-label='Home' component={RouterLink} to='/'>
                        <Home />
                      </IconButton>
                    </>
                  )
                }
                <img src={store.mainAgencyLogo} alt={mainAgency.name} style={{ height: '56px', paddingRight: '32px', visibility: store.mainAgencyLogo ? 'visible' : 'hidden' }} />
                {isWidthUp('sm', this.props.width) && (
                  <>
                    <Typography variant='h6' color='inherit' style={{ flexGrow: 1 }}>
                      {mainAgency.name}
                    </Typography>
                    <Typography variant='subtitle1' color='inherit'>
                      {mainAgency.emailAddress && (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Email fontSize='inherit' />
                          &nbsp;
                          {mainAgency.emailAddress}
                        </span>
                      )}
                      {' - '}
                      {mainAgency.phone && (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Phone fontSize='inherit' />
                          &nbsp;
                          {parsePhoneNumberFromString(mainAgency.phone, 'FR').format(navigator.language === 'fr-FR' ? 'NATIONAL' : 'INTERNATIONAL')}
                        </span>
                      )}
                    </Typography>
                  </>
                )}
              </Toolbar>
            </AppBar>
            <div style={{ paddingTop: '64px' }}>
              <Routes>
                <Route path='/' element={<HomePage />} />
                <Route path='/listing/:listingId/:startDate/:endDate' element={<ListingPageRoute />} />
                <Route path='/listing/:listingId' element={<ListingPageRoute />} />
                <Route path='/book/:listingId' element={<BookPageRoute />} />
                <Route path='/book' element={<BookPageRoute />} />
                <Route path='*' element={<Navigate to='/' replace />} />
              </Routes>
            </div>
          </div>
          <footer style={{
            backgroundColor: theme.palette.background.paper,
            marginTop: theme.spacing(8),
            padding: `${theme.spacing(6)} 0`
          }}
          >
            <Grid container justifyContent='space-evenly' alignItems='flex-end' style={{ width: '100%' }}>
              <Grid item>
                <Typography variant='h6' align='center' gutterBottom>
                  {mainAgency.name}
                </Typography>
                {[mainAgency.address1, mainAgency.address2, mainAgency.address3].filter(v => v !== '').map((v, i) => (
                  <Typography variant='subtitle1' align='center' color='textSecondary' key={i}>
                    {v}
                  </Typography>
                ))}
                <Typography variant='subtitle1' align='center' color='textSecondary'>
                  {mainAgency.postCode} {mainAgency.city}
                </Typography>
                <Typography variant='subtitle1' align='center' color='textSecondary'>
                  {mainAgency.emailAddress && (
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Email fontSize='inherit' />
                      &nbsp;
                      {mainAgency.emailAddress}
                    </span>
                  )}
                  {' - '}
                  {mainAgency.phone && (
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Phone fontSize='inherit' />
                      &nbsp;
                      {parsePhoneNumberFromString(mainAgency.phone, 'FR').format(navigator.language === 'fr-FR' ? 'NATIONAL' : 'INTERNATIONAL')}
                    </span>
                  )}
                </Typography>
                <Typography variant='subtitle1' align='center'>
                  <Link href={`https://www.homesejour.fr/login/${mainAgency.agencyId}`} target='_blank'>
                    <FormattedMessage
                      defaultMessage='Owner access'
                      id='ReservationEnLigne.Components.App.OwnerAccess'
                    />
                  </Link>
                </Typography>
              </Grid>
              <Grid item>
                <Link href='https://www.septeo-adb.fr/' target='_blank'>
                  <img src={LogoSPI} alt='Septeo ADB Logo' style={{ display: 'block', margin: 'auto', padding: '10px', width: '200px' }} />
                </Link>
                <Typography variant='subtitle1' align='center' color='textSecondary' gutterBottom>
                  <FormattedMessage
                    defaultMessage='© {year}, created by Septeo ADB'
                    id='ReservationEnLigne.Components.App.Copyright'
                    values={{
                      year: new Date().getFullYear()
                    }}
                  />
                </Typography>
                <Typography variant='subtitle1' align='center' color='textSecondary' gutterBottom>
                  <Link href='https://www.septeo-adb.fr/' target='_blank'>
                    https://www.septeo-adb.fr/
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </footer>
        </ThemeProvider>
      )
    }
  }
}

export default withRouter(App)
